import React, { memo, useMemo } from 'react';
import { getClefForNote } from '../utils/noteHelpers';

// Y positions for notes on the treble staff (offset by STAFF_OFFSET_Y = 120)
// Added extra 40px at top to ensure high notes aren't clipped
// Treble clef lines from bottom to top: E4, G4, B4, D5, F5
// Treble clef spaces from bottom to top: F4, A4, C5, E5
// G5 is the space ABOVE the top line (F5)
const TREBLE_NOTE_POSITIONS = {
  // High notes above staff (with ledger lines)
  'C6': 89,   // 2 ledger lines above
  'B5': 101,  // space between ledger lines
  'A5': 112,  // 1 ledger line above staff
  // Standard treble staff range
  'G5': 124,  // Space above top line F5
  'F5': 135,  // Top LINE of treble staff        (SVG y=15.30 + 120)
  'E5': 147,  // Space below top line
  'D5': 158,  // 4th line                        (SVG y=38.38 + 120)
  'C5': 169,  // Space
  'B4': 181,  // 3rd line (middle)               (SVG y=60.50 + 120)
  'A4': 192,  // Space
  'G4': 204,  // 2nd line                        (SVG y=83.58 + 120)
  'F4': 215,  // Space
  'E4': 226,  // Bottom LINE of treble staff     (SVG y=105.70 + 120)
  'D4': 237,  // Space below staff
  'C4': 248,  // Middle C - 1 ledger line below treble
};

// Y positions for notes on the bass staff (offset by STAFF_OFFSET_Y = 120)
// Bass clef lines from bottom to top: G2, B2, D3, F3, A3
// Bass clef spaces from bottom to top: A2, C3, E3, G3
// B3 is the space ABOVE the top line (A3)
const BASS_NOTE_POSITIONS = {
  // Notes above bass staff (with ledger lines)
  'C4': 307,  // Middle C - 1 ledger line above bass
  'B3': 318,  // Space above top line
  // Standard bass staff range
  'A3': 329,  // Top LINE of bass staff          (SVG y=208.60 + 120)
  'G3': 340,  // Space below top line
  'F3': 351,  // 4th line                        (SVG y=230.72 + 120)
  'E3': 362,  // Space
  'D3': 374,  // 3rd line (middle)               (SVG y=253.80 + 120)
  'C3': 385,  // Space
  'B2': 396,  // 2nd line                        (SVG y=275.92 + 120)
  'A2': 407,  // Space
  'G2': 419,  // Bottom LINE of bass staff       (SVG y=299.00 + 120)
  // Low notes below staff (with ledger lines)
  'F2': 430,  // Space below bottom line
  'E2': 442,  // 1 ledger line below
  'D2': 453,  // Space
  'C2': 465,  // 2 ledger lines below
};

// Treble clef staff lines (from bottom to top): E4, G4, B4, D5, F5
const TREBLE_LINE_NOTES = ['E4', 'G4', 'B4', 'D5', 'F5'];
// Treble clef staff spaces (from bottom to top): F4, A4, C5, E5
const TREBLE_SPACE_NOTES = ['F4', 'A4', 'C5', 'E5'];

// Bass clef staff lines (from bottom to top): G2, B2, D3, F3, A3
const BASS_LINE_NOTES = ['G2', 'B2', 'D3', 'F3', 'A3'];
// Bass clef staff spaces (from bottom to top): A2, C3, E3, G3
const BASS_SPACE_NOTES = ['A2', 'C3', 'E3', 'G3'];

// Y positions for treble clef lines (5 lines from bottom to top): E4, G4, B4, D5, F5 (offset by STAFF_OFFSET_Y = 120)
const TREBLE_LINE_Y_POSITIONS = [226, 204, 181, 158, 135];
// Y positions for treble clef spaces (4 spaces from bottom to top): F4, A4, C5, E5 (offset by STAFF_OFFSET_Y = 120)
const TREBLE_SPACE_Y_POSITIONS = [215, 192, 169, 147];

// Y positions for bass clef lines (5 lines from bottom to top): G2, B2, D3, F3, A3 (offset by STAFF_OFFSET_Y = 120)
const BASS_LINE_Y_POSITIONS = [419, 396, 374, 351, 329];
// Y positions for bass clef spaces (4 spaces from bottom to top): A2, C3, E3, G3 (offset by STAFF_OFFSET_Y = 120)
const BASS_SPACE_Y_POSITIONS = [407, 385, 362, 340];

// SVG viewBox dimensions - expanded to show notes above/below staff
const STAFF_WIDTH = 380;
const STAFF_HEIGHT = 500; // Extra space for notes above/below staff
const STAFF_OFFSET_Y = 120; // Vertical offset for the staff image (more space above for high notes)

// Whole note SVG path (from Note.svg - 30x20)
const NOTE_PATH = "M14.3132 0.0158635C18.767 -0.163023 23.7767 1.16748 27.1638 4.18676C30.4302 7.0987 31.1138 11.9893 27.9558 15.2659C24.6972 18.6473 20.2062 19.8301 15.695 19.9993C10.8759 20.0354 6.30404 18.9304 2.49775 15.6946C1.07763 14.4513 0.125624 12.4771 0.014353 10.5881C-0.332819 4.68904 5.68691 1.42995 10.6013 0.42602C11.8239 0.176323 13.0747 0.0868781 14.3132 0.0158635ZM18.0563 3.99047C15.6317 1.85688 11.6489 0.0381614 8.84052 2.52563C7.94132 3.3222 7.32901 4.58428 7.26435 5.80102C7.13921 9.0244 8.3767 11.8073 10.281 14.2707C12.1311 16.5237 14.4791 18.3701 17.4421 18.7219C20.3136 19.0631 22.488 16.7969 22.6628 13.9778C22.8438 11.0547 21.5471 7.95823 19.7145 5.73852C19.1943 5.12949 18.6596 4.52138 18.0563 3.99047Z";

// Note with ledger line path (from Note-Line.svg - 44x20, line at y=9)
const NOTE_LINE_PATH = "M21.3117 0.0158541C25.7655 -0.162975 30.7753 1.16752 34.1623 4.18675C37.4286 7.09869 38.1113 11.9893 34.9533 15.2659C31.6949 18.6472 27.2047 19.8301 22.6936 19.9993C17.8743 20.0354 13.3017 18.9305 9.49534 15.6946C8.07545 14.4513 7.1242 12.4778 7.01292 10.5891C6.66541 4.68981 12.6844 1.431 17.5989 0.426987C18.8217 0.177229 20.0731 0.0868766 21.3117 0.0158541ZM25.0539 3.99144C22.6292 1.85778 18.6465 0.0387965 15.8381 2.5266C14.939 3.32319 14.3266 4.58534 14.2619 5.80199C14.1369 9.02531 15.3752 11.8083 17.2795 14.2717C19.1297 16.5247 21.4776 18.3702 24.4407 18.7219C27.312 19.0629 29.4854 16.7977 29.6604 13.9787C29.8415 11.0556 28.5447 7.95925 26.7121 5.73949C26.1918 5.13046 25.6572 4.52234 25.0539 3.99144Z";

// Notes that need ledger lines (above treble staff)
const TREBLE_LEDGER_NOTES = {
  'A5': [112],        // 1 ledger line at A5 position
  'B5': [112],        // 1 ledger line (same position as A5 line)
  'C6': [112, 89],    // 2 ledger lines
};

// Notes that need ledger lines (below treble staff - D4, C4)
const TREBLE_BELOW_LEDGER_NOTES = {
  'C4': [248],        // Middle C ledger line
};

// Notes that need ledger lines (below bass staff)
const BASS_LEDGER_NOTES = {
  'E2': [442],        // 1 ledger line
  'D2': [442],        // 1 ledger line
  'C2': [442, 465],   // 2 ledger lines
};

// Notes that need ledger lines (above bass staff - B3, C4)
const BASS_ABOVE_LEDGER_NOTES = {
  'C4': [307],        // Middle C ledger line on bass staff
};

// Separate component for the note to avoid React span wrapper issue
const NoteGraphic = memo(function NoteGraphic({ noteX, notePosition, note, clef }) {
  // Determine which ledger lines to draw
  let ledgerLines = [];

  if (clef === 'treble') {
    if (TREBLE_LEDGER_NOTES[note]) {
      ledgerLines = TREBLE_LEDGER_NOTES[note];
    } else if (TREBLE_BELOW_LEDGER_NOTES[note]) {
      ledgerLines = TREBLE_BELOW_LEDGER_NOTES[note];
    }
  } else {
    if (BASS_LEDGER_NOTES[note]) {
      ledgerLines = BASS_LEDGER_NOTES[note];
    } else if (BASS_ABOVE_LEDGER_NOTES[note]) {
      ledgerLines = BASS_ABOVE_LEDGER_NOTES[note];
    }
  }

  const isOnLedgerLine = note === 'C4' || note === 'A5' || note === 'C6' ||
                         note === 'E2' || note === 'C2';

  const noteTransform = isOnLedgerLine
    ? `translate(${noteX - 22}, ${notePosition - 10})`
    : `translate(${noteX - 15}, ${notePosition - 10})`;

  const notePath = isOnLedgerLine ? NOTE_LINE_PATH : NOTE_PATH;

  // Build ledger line segments into the note path itself
  const ledgerPathD = ledgerLines.length > 0
    ? ledgerLines.map(lineY => `M ${noteX - 30} ${lineY} L ${noteX + 30} ${lineY}`).join(' ')
    : `M 0 0`;

  return (
    <g className="note-group">
      <path
        className="note-oval"
        d={notePath}
        fill="#2B251F"
        fillRule="evenodd"
        transform={noteTransform}
      />
      <path className="ledger-lines" d={ledgerPathD} stroke="#504C48" strokeWidth="2" fill="none" />
    </g>
  );
});

// Component to render note labels for learning mode
const StaffNoteLabels = memo(function StaffNoteLabels({ clef, isNoteOnLine }) {
  const labelX = 350; // Position labels to the right of the note
  
  if (clef === 'treble') {
    if (isNoteOnLine) {
      // Show line note labels for treble clef: E, G, B, D, F (bottom to top)
      return (
        <g className="staff-labels">
          {TREBLE_LINE_NOTES.map((noteName, index) => (
            <text 
              key={noteName} 
              x={labelX} 
              y={TREBLE_LINE_Y_POSITIONS[index] + 4} 
              textAnchor="start"
              fill="#8B8680"
              fontSize="13"
              fontWeight="500"
              dominantBaseline="middle"
            >
              {noteName[0]}
            </text>
          ))}
        </g>
      );
    } else {
      // Show space note labels for treble clef: F, A, C, E (bottom to top)
      return (
        <g className="staff-labels">
          {TREBLE_SPACE_NOTES.map((noteName, index) => (
            <text 
              key={noteName} 
              x={labelX} 
              y={TREBLE_SPACE_Y_POSITIONS[index] + 4} 
              textAnchor="start"
              fill="#8B8680"
              fontSize="13"
              fontWeight="500"
              dominantBaseline="middle"
            >
              {noteName[0]}
            </text>
          ))}
        </g>
      );
    }
  } else {
    // Bass clef
    if (isNoteOnLine) {
      // Show line note labels for bass clef: G, B, D, F, A (bottom to top)
      return (
        <g className="staff-labels">
          {BASS_LINE_NOTES.map((noteName, index) => (
            <text 
              key={noteName} 
              x={labelX} 
              y={BASS_LINE_Y_POSITIONS[index] + 4} 
              textAnchor="start"
              fill="#8B8680"
              fontSize="13"
              fontWeight="500"
              dominantBaseline="middle"
            >
              {noteName[0]}
            </text>
          ))}
        </g>
      );
    } else {
      // Show space note labels for bass clef: A, C, E, G (bottom to top)
      return (
        <g className="staff-labels">
          {BASS_SPACE_NOTES.map((noteName, index) => (
            <text 
              key={noteName} 
              x={labelX} 
              y={BASS_SPACE_Y_POSITIONS[index] + 4} 
              textAnchor="start"
              fill="#8B8680"
              fontSize="13"
              fontWeight="500"
              dominantBaseline="middle"
            >
              {noteName[0]}
            </text>
          ))}
        </g>
      );
    }
  }
});

const MusicStaff = memo(function MusicStaff({ note, showNoteName = false, learningMode = false }) {
  const clef = useMemo(() => getClefForNote(note), [note]);
  
  const notePosition = useMemo(() => {
    if (clef === 'treble') {
      return TREBLE_NOTE_POSITIONS[note] ?? 60;
    } else {
      return BASS_NOTE_POSITIONS[note] ?? 253;
    }
  }, [note, clef]);

  // Determine if the current note is on a line or in a space
  const isNoteOnLine = useMemo(() => {
    if (clef === 'treble') {
      // Treble clef line notes: E4, G4, B4, D5, F5, also C4 (ledger line)
      return TREBLE_LINE_NOTES.includes(note) || note === 'C4';
    } else {
      // Bass clef line notes: G2, B2, D3, F3, A3
      return BASS_LINE_NOTES.includes(note);
    }
  }, [note, clef]);

  // Get the labels to display based on clef and line/space
  const labelsToShow = useMemo(() => {
    // Positions adjusted for expanded viewBox (500 total height, staff starts at y=120)
    // Formula: (yPosition / 500) * 100 to get percentage
    if (clef === 'treble') {
      if (isNoteOnLine) {
        // Line notes for treble: E4, G4, B4, D5, F5 (bottom to top of treble staff) + C4 (ledger line)
        return { notes: ['F5', 'D5', 'B4', 'G4', 'E4', 'C4'], positions: [27.0, 31.6, 36.2, 40.8, 45.2, 49.6] };
      } else {
        // Space notes for treble: F4, A4, C5, E5 (bottom to top) + D4 (below staff)
        // E5=158, C5=180, A4=202, F4=224, D4=246
        return { notes: ['E5', 'C5', 'A4', 'F4', 'D4'], positions: [29.4, 33.8, 38.4, 43.0, 47.4] };
      }
    } else {
      if (isNoteOnLine) {
        // Line notes for bass: G2, B2, D3, F3, A3 (bottom to top of bass staff)
        // A3=337, F3=359, D3=381, B2=403, G2=425
        return { notes: ['A3', 'F3', 'D3', 'B2', 'G2'], positions: [65.8, 70.2, 74.8, 79.2, 83.8] };
      } else {
        // Space notes for bass: A2, C3, E3, G3 (bottom to top) + B3 (above staff)
        // B3=326, G3=348, E3=370, C3=392, A2=414
        return { notes: ['B3', 'G3', 'E3', 'C3', 'A2'], positions: [63.6, 68.0, 72.4, 77.0, 81.4] };
      }
    }
  }, [clef, isNoteOnLine]);

  const noteX = 220;

  return (
    <div className="w-full h-full flex flex-col items-center justify-center overflow-visible" data-testid="music-staff">
      <div 
        className="relative rounded-card transition-all duration-300 overflow-visible"
        style={{ maxWidth: '100%', width: 'auto' }}
      >
        {/* Learning mode labels - positioned to the right of the staff */}
        {learningMode && (
          <div 
            className="absolute top-0 bottom-0 flex flex-col justify-start pointer-events-none" 
            style={{ left: 'calc(100% + 8px)', width: '20px' }}
          >
            {labelsToShow.notes.map((noteName, index) => (
              <span
                key={noteName}
                className="absolute text-[13px] font-sans font-semibold select-none leading-none"
                style={{ 
                  top: `${labelsToShow.positions[index]}%`,
                  left: '0',
                  transform: 'translateY(-50%)',
                  color: '#6B7F8C'
                }}
              >
                {noteName[0]}
              </span>
            ))}
          </div>
        )}
        
        <svg 
          viewBox={`0 0 ${STAFF_WIDTH} ${STAFF_HEIGHT}`}
          className="w-full h-auto max-h-[280px] sm:max-h-[320px] md:max-h-[380px]"
          style={{ minWidth: '250px', maxWidth: '420px' }}
          aria-label={`Musical note ${note} on ${clef} clef`}
          role="img"
        >
          {/* Staff background with clefs and brace - offset to center in expanded viewBox */}
          <image 
            href="/note-staff.svg" 
            x="0" 
            y={STAFF_OFFSET_Y} 
            width={STAFF_WIDTH} 
            height="300"
          />
          
          {/* Note graphic - using separate component to avoid span wrapper */}
          <NoteGraphic 
            noteX={noteX} 
            notePosition={notePosition} 
            note={note}
            clef={clef}
          />
        </svg>
      </div>
      
      {/* Note name reveal (shown after incorrect answer) */}
      {showNoteName && (
        <div 
          className="mt-6 note-label-reveal"
          aria-live="polite"
        >
          <span className="bg-cream-900 text-white px-6 py-3 rounded-full text-xl font-serif tracking-wide shadow-lg">
            {note}
          </span>
        </div>
      )}
    </div>
  );
});

export default MusicStaff;
